import { useEffect, useRef, useCallback } from 'react';

const USER_SCROLL_PAUSE_MS = 4000;

/**
 * Auto-scrolls a playlist container to keep the currently playing track
 * visible.  Pauses when the user manually scrolls and resumes after a
 * period of inactivity.
 *
 * @param {React.RefObject} containerRef - ref to the scrollable `.export-tracklist` element
 * @param {string|null} activeTrackId   - the id of the currently playing track (null when nothing is playing)
 */
export function useAutoScrollPlaylist(containerRef, activeTrackId) {
  const userScrollingRef = useRef(false);
  const pauseTimerRef = useRef(null);
  // Track whether the last scroll event was triggered programmatically so
  // we can ignore it in the user-scroll detector.
  const programmaticScrollRef = useRef(false);

  // Detect manual (user-initiated) scrolls and pause auto-scroll
  const handleScroll = useCallback(() => {
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }

    userScrollingRef.current = true;
    clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      userScrollingRef.current = false;
    }, USER_SCROLL_PAUSE_MS);
  }, []);

  // Attach / detach the scroll listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(pauseTimerRef.current);
    };
  }, [containerRef, handleScroll]);

  // When the active track changes, scroll it into view (unless user is scrolling)
  useEffect(() => {
    if (!activeTrackId || userScrollingRef.current) return;
    const el = containerRef.current;
    if (!el) return;

    const row = el.querySelector(`[data-track-id="${CSS.escape(activeTrackId)}"]`);
    if (!row) return;

    // Check if the row is already fully visible — skip scroll if so
    const containerRect = el.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom) return;

    programmaticScrollRef.current = true;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeTrackId, containerRef]);
}
